import { jsx as _jsx } from "react/jsx-runtime";
import { convertUnknownToError, reportErrorToBugsnag } from "@air/utils-error";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { THUMBNAIL_PREFERENCES } from "~/constants/localStorageKeys";
import { currentThumbnailPreferenceSelector } from "~/store/configViews/selectors";
import { isDevOrTestStage } from "~/swr-hooks/utils";
const THUMBNAIL_PREFERENCES_CLASS_NAME_MAP = {
    default: "object-cover",
    contain: "object-contain"
};
export const getPreferenceClassName = (preference)=>{
    if (preference) {
        return THUMBNAIL_PREFERENCES_CLASS_NAME_MAP[preference];
    }
    return "";
};
export const getPreferenceImgixFit = (preference)=>preference === "default" ? "crop" : "clip";
export const ThumbnailPreferenceContext = /*#__PURE__*/ createContext(null);
export const ThumbnailPreferenceContextProvider = (param)=>{
    let { children } = param;
    const [preferences, setPreferences] = useState([]);
    const configViewThumbnailPreference = useSelector(currentThumbnailPreferenceSelector);
    useEffect(()=>{
        const update = ()=>{
            const preferences = localStorage.getItem(THUMBNAIL_PREFERENCES);
            if (preferences) {
                try {
                    const parsedPreferences = JSON.parse(preferences);
                    const updatedPreferences = parsedPreferences.map((preference)=>{
                        // @ts-expect-error `fill` is not a supported option, we need to convert it to `default` if present
                        if (preference.value === "fill") {
                            return {
                                ...preference,
                                value: "default"
                            };
                        }
                        return preference;
                    });
                    setPreferences(updatedPreferences);
                } catch (error) {
                    if (isDevOrTestStage()) {
                        console.error("Error parsing thumbnail preferences", error);
                    } else {
                        reportErrorToBugsnag({
                            error: convertUnknownToError(error),
                            context: "Error parsing thumbnail preferences"
                        });
                    }
                }
            }
        };
        window.addEventListener("storage", update);
        update();
        return ()=>{
            window.removeEventListener("storage", update);
        };
    }, []);
    const updatePreference = useCallback((boardId, value)=>{
        let newPreferences;
        if (preferences.find((preference)=>preference.id === boardId)) {
            newPreferences = preferences.map((preference)=>{
                if (preference.id === boardId) {
                    return {
                        ...preference,
                        value
                    };
                }
                return preference;
            });
        } else {
            newPreferences = [
                ...preferences,
                {
                    id: boardId,
                    value
                }
            ];
        }
        setPreferences(newPreferences);
        localStorage.setItem(THUMBNAIL_PREFERENCES, JSON.stringify(newPreferences));
    }, [
        preferences
    ]);
    const getPreference = useCallback((boardId)=>{
        const getValue = ()=>{
            var _preferences_find;
            if (configViewThumbnailPreference) {
                return configViewThumbnailPreference;
            }
            if (!boardId) {
                return undefined;
            }
            return (preferences === null || preferences === void 0 ? void 0 : (_preferences_find = preferences.find((preference)=>preference.id === boardId)) === null || _preferences_find === void 0 ? void 0 : _preferences_find.value) || "default";
        };
        const value = getValue();
        // @ts-expect-error `fill` is not a supported option, we need to convert it to `default` if present
        return value === "fill" ? "default" : value;
    }, [
        preferences,
        configViewThumbnailPreference
    ]);
    const value = useMemo(()=>({
            getPreference,
            updatePreference
        }), [
        getPreference,
        updatePreference
    ]);
    return /*#__PURE__*/ _jsx(ThumbnailPreferenceContext.Provider, {
        value: value,
        children: children
    });
};
export const useThumbnailPreference = ()=>{
    const context = useContext(ThumbnailPreferenceContext);
    if (!context) {
        throw new Error("useThumbnailPreference must be used within ThumbnailPreferenceContextProvider");
    }
    return context;
};
